/**
 * The transport: one base URL, the bearer token on every call, and the
 * API's error envelope turned into a typed error.
 *
 * Configured once by the app (`configureApi`) rather than reading
 * `import.meta.env` here, so the package has no build-tool coupling and
 * tests can point it at a stub.
 */

export interface ApiErrorBody {
  error: string;
  message?: string;
  correlation_id?: string;
  /** Structured facts for the caller to act on (e.g. `service_required`'s service). */
  details?: Record<string, unknown>;
  [key: string]: unknown;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorBody,
  ) {
    super(body.message ?? body.error ?? `HTTP ${status}`);
    this.name = 'ApiError';
  }

  /** The stable discriminator the API guarantees; the message is not a contract. */
  get code(): string {
    return this.body.error;
  }

  get correlationId(): string | undefined {
    return this.body.correlation_id;
  }
}

/** Compatibility alias for call sites written against the Phase 0 client. */
export const TenantsApiError = ApiError;
export type TenantsApiError = ApiError;

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

let baseUrl = '';
let fetchImpl: FetchLike | null = null;

export function configureApi(options: { baseUrl: string | undefined; fetch?: FetchLike }): void {
  baseUrl = (options.baseUrl ?? '').replace(/\/$/, '');
  fetchImpl = options.fetch ?? null;
}

export function apiBaseUrl(): string {
  return baseUrl;
}

export type QueryParams = Record<string, string | number | undefined | null>;

/** Drops empty values; encodes the rest. */
export function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

export async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  idToken: string,
  init: { body?: unknown; query?: QueryParams } = {},
): Promise<T> {
  if (!baseUrl) {
    throw new ApiError(0, {
      error: 'config_missing',
      message:
        'The API base URL is not configured. Re-run scripts/deploy-web.sh so ' +
        'VITE_INTEGRATIONS_API_URL is baked into the bundle.',
    });
  }
  const doFetch: FetchLike = fetchImpl ?? ((input, options) => fetch(input, options));
  const response = await doFetch(`${baseUrl}${path}${buildQuery(init.query)}`, {
    method,
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });
  return readBody<T>(response);
}

async function readBody<T>(response: Response): Promise<T> {
  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    throw new ApiError(response.status, {
      error: 'invalid_response',
      message: `The API returned a non-JSON body (status ${response.status}).`,
    });
  }
  if (!response.ok) {
    // Defensive: API Gateway and Lambda platform errors do not use our
    // envelope. Fill in the discriminator so callers can still branch.
    const body: ApiErrorBody =
      parsed && typeof parsed === 'object' ? (parsed as ApiErrorBody) : { error: 'unknown' };
    if (!body.error) body.error = `http_${response.status}`;
    if (!body.message) body.message = response.statusText || `Status ${response.status}`;
    throw new ApiError(response.status, body);
  }
  return parsed as T;
}

/** A one-line description for an error panel: code, message and correlation id. */
export function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    const parts = [err.code, err.message].filter(Boolean);
    if (err.correlationId) parts.push(`(ref ${err.correlationId})`);
    return parts.join(': ').replace(': (', ' (');
  }
  if (err instanceof Error) return err.message || err.name;
  return String(err) || 'Unknown error';
}
